import { useLayoutEffect, useRef, useState } from "react";

/**
 * Opt-in cap for single-table listing pages. Keep the caller's existing maximum,
 * but reserve the actual toolbar/header/footer and surrounding page spacing.
 * Small screens keep a usable row area and the existing outer-scroll fallback.
 */
export const useViewportTableHeight = (
  enabled: boolean,
  heightDiff = 230,
  minimumHeight = 100,
) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [maxHeight, setMaxHeight] = useState<number>();

  useLayoutEffect(() => {
    if (!enabled) return;
    const container = containerRef.current;
    if (!container) return;
    const scrollParent = container.closest<HTMLElement>(".main-container");
    let frame = 0;
    let scroller: HTMLElement | null = null;
    const findScroller = () => container.querySelector<HTMLElement>(
      ".MuiDataGrid-virtualScroller",
    );

    const measure = () => {
      // MUI mounts (and can replace) this node after our first layout effect.
      // Keep observing the container while it is absent instead of opting out.
      const currentScroller = findScroller();
      if (currentScroller !== scroller) {
        if (scroller) observer?.unobserve(scroller);
        scroller = currentScroller;
        if (scroller) observer?.observe(scroller);
      }
      if (!scroller) return;
      const bounds = container.getBoundingClientRect();
      const scrollerBounds = scroller.getBoundingClientRect();
      // Hidden/unmounted grids and non-layout environments retain legacy sizing.
      if (!bounds.height || !scrollerBounds.height) return;
      const bottom = scrollParent
        ? scrollParent.getBoundingClientRect().top +
          scrollParent.clientTop + scrollParent.clientHeight
        : window.innerHeight;
      const scrollOffset = scrollParent ? scrollParent.scrollTop : window.scrollY;
      let bottomSpacing = 0;
      let ancestor: HTMLElement | null = container;
      while (ancestor) {
        const style = getComputedStyle(ancestor);
        // Container chrome already includes its own padding/border.
        if (ancestor !== container) {
          bottomSpacing += parseFloat(style.paddingBottom) || 0;
          if (ancestor !== scrollParent) {
            bottomSpacing += parseFloat(style.borderBottomWidth) || 0;
          }
        }
        if (ancestor === scrollParent) break;
        bottomSpacing += parseFloat(style.marginBottom) || 0;
        ancestor = ancestor.parentElement;
      }
      const chromeHeight = bounds.height - scrollerBounds.height;
      const available = bottom - bounds.top - scrollOffset - chromeHeight - bottomSpacing;
      setMaxHeight(Math.max(
        minimumHeight,
        Math.floor(Math.min(window.innerHeight - heightDiff, available)),
      ));
    };

    const scheduleMeasure = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(measure);
    };
    const observer = typeof ResizeObserver !== "undefined"
      ? new ResizeObserver(scheduleMeasure)
      : undefined;
    // Watch filter/breadcrumb wrapping as well as grid chrome and viewport size.
    let ancestor: HTMLElement | null = container;
    while (ancestor) {
      observer?.observe(ancestor);
      Array.from(ancestor.children).forEach((child) => observer?.observe(child));
      if (ancestor === scrollParent) break;
      ancestor = ancestor.parentElement;
    }
    const mutationObserver = new MutationObserver(() => {
      // Ignore row virtualization and other DOM changes that do not replace the
      // scroller; its dimensions are already covered by ResizeObserver.
      if (findScroller() !== scroller) scheduleMeasure();
    });
    mutationObserver.observe(container, { childList: true, subtree: true });
    measure();
    window.addEventListener("resize", scheduleMeasure);
    return () => {
      observer?.disconnect();
      mutationObserver.disconnect();
      cancelAnimationFrame(frame);
      window.removeEventListener("resize", scheduleMeasure);
    };
  }, [enabled, heightDiff, minimumHeight]);

  return { containerRef, maxHeight: enabled ? maxHeight : undefined };
};
